"use client"

import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { useCallback, useEffect, useRef, useState } from "react"

import { ChevronDownMini, ChevronUpMini, XMarkMini } from "@medusajs/icons"
import { Text, clx, Button, Drawer } from "@medusajs/ui"
import { SlidersHorizontal, ChevronRight } from "lucide-react"
import FilterRadioGroup from "@modules/common/components/filter-radio-group"
import CheckboxWithLabel from "@modules/common/components/checkbox"

import SortProducts, { SortOptions } from "./sort-products"

type CategoryTree = {
  id: string
  name: string
  category_children?: CategoryTree[]
}

type FilterData = {
  collections: { id: string; title: string }[]
  categories: { id: string; name: string }[]
  categoriesTree: CategoryTree[]
  tags: string[]
  brands: string[]
}

type RefinementListProps = {
  sortBy: SortOptions
  search?: boolean
  'data-testid'?: string
  collection?: string
  category?: string
  minPrice?: string
  maxPrice?: string
  tags?: string
  brand?: string
  filterData?: FilterData
}

type AccordionSectionProps = {
  title: string
  isOpen: boolean
  onToggle: () => void
  children: React.ReactNode
}

/**
 * Un valor de precio solo es aplicable si está vacío o es un número >= 0.
 * Antes se navegaba con cada tecla: valores intermedios ("-", "1e") llegaban
 * a Meili como NaN, la búsqueda devolvía 400 y el listado se quedaba vacío.
 */
function isValidPriceInput(value: string): boolean {
  if (value === "") return true

  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed >= 0
}

const AccordionSection = ({ title, isOpen, onToggle, children }: AccordionSectionProps) => {
  return (
    <div className="border-b border-ui-border py-3">
      <button
        onClick={onToggle}
        className="flex items-center justify-between w-full py-2 text-left"
      >
        <Text className="txt-compact-medium-plus">{title}</Text>
        {isOpen ? (
          <ChevronUpMini className="text-ui-fg-muted" />
        ) : (
          <ChevronDownMini className="text-ui-fg-muted" />
        )}
      </button>
      <div
        className={clx("overflow-hidden transition-all duration-200", {
          "max-h-0": !isOpen,
          "max-h-[500px]": isOpen,
        })}
      >
        <div className="pt-2 pb-4">{children}</div>
      </div>
    </div>
  )
}

// Category tree item component for hierarchical display
const CategoryTreeItem = ({
  category,
  selectedCategories,
  onToggle,
  depth = 0,
}: {
  category: CategoryTree
  selectedCategories: string[]
  onToggle: (name: string, value: string, currentSelected: string[]) => void
  depth?: number
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const hasChildren = category.category_children && category.category_children.length > 0

  return (
    <div className="flex flex-col">
      <div
        className="flex items-center gap-1"
        style={{ paddingLeft: `${depth * 12}px` }}
      >
        {hasChildren && (
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="p-0.5 hover:bg-gray-100 rounded"
          >
            <ChevronRight
              className={`w-3 h-3 text-gray-400 transition-transform ${isOpen ? "rotate-90" : ""}`}
            />
          </button>
        )}
        {!hasChildren && <div className="w-4" />}
        <CheckboxWithLabel
          label={category.name}
          checked={selectedCategories.includes(category.id)}
          onChange={() => onToggle("category", category.id, selectedCategories)}
        />
      </div>
      {hasChildren && isOpen && (
        <div className="flex flex-col">
          {category.category_children!.map((child) => (
            <CategoryTreeItem
              key={child.id}
              category={child}
              selectedCategories={selectedCategories}
              onToggle={onToggle}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// Filter content component to reuse in both desktop and mobile
const FilterContent = ({
  sortBy,
  dataTestId,
  collection,
  category,
  minPrice,
  maxPrice,
  tags,
  brand,
  filterData,
  onClearFilters,
  setQueryParams,
  minPriceInput,
  maxPriceInput,
  onPriceInputChange,
  handleToggle,
  selectedCollections,
  selectedCategories,
  selectedBrands,
  selectedTags,
  openSections,
  setOpenSections,
}: {
  sortBy: SortOptions
  dataTestId?: string
  collection?: string
  category?: string
  minPrice?: string
  maxPrice?: string
  tags?: string
  brand?: string
  filterData?: FilterData
  onClearFilters: () => void
  setQueryParams: (name: string, value: string) => void
  minPriceInput: string
  maxPriceInput: string
  onPriceInputChange: (type: "min" | "max", value: string) => void
  handleToggle: (name: string, value: string, currentSelected: string[]) => void
  selectedCollections: string[]
  selectedCategories: string[]
  selectedBrands: string[]
  selectedTags: string[]
  openSections: Record<string, boolean>
  setOpenSections: React.Dispatch<React.SetStateAction<Record<string, boolean>>>
}) => {
  const collections = filterData?.collections || []
  const categories = filterData?.categories || []
  const categoriesTree = filterData?.categoriesTree || []
  const productTags = filterData?.tags || []
  const productBrands = filterData?.brands || []
  const [brandSearch, setBrandSearch] = useState("")
  const [tagSearch, setTagSearch] = useState("")

  const filteredBrands = brandSearch
    ? productBrands.filter((b) => b.toLowerCase().includes(brandSearch.toLowerCase()))
    : productBrands

  const filteredTags = tagSearch
    ? productTags.filter((t) => t.toLowerCase().includes(tagSearch.toLowerCase()))
    : productTags

  return (
    <>
      <SortProducts sortBy={sortBy} setQueryParams={setQueryParams} data-testid={dataTestId} />

      {/* Clear filters button */}
      {(collection || category || minPrice || maxPrice || tags || brand) && (
        <button
          onClick={onClearFilters}
          className="text-sm text-ui-fg-muted hover:text-ui-fg-base py-2 text-left underline"
        >
          Limpiar filtros
        </button>
      )}

      {/* Collections filter */}
      <AccordionSection
        title="Colecciones"
        isOpen={openSections.collections}
        onToggle={() => setOpenSections((prev) => ({ ...prev, collections: !prev.collections }))}
      >
        {collections.length > 0 ? (
          <div className="flex flex-col gap-2">
            {collections.map((c) => (
              <CheckboxWithLabel
                key={c.id}
                label={c.title}
                checked={selectedCollections.includes(c.id)}
                onChange={() => handleToggle("collection", c.id, selectedCollections)}
              />
            ))}
          </div>
        ) : (
          <Text className="txt-compact-small text-ui-fg-muted">Sin opciones</Text>
        )}
      </AccordionSection>

      {/* Categories filter with hierarchy */}
      <AccordionSection
        title="Categorías"
        isOpen={openSections.categories}
        onToggle={() => setOpenSections((prev) => ({ ...prev, categories: !prev.categories }))}
      >
        {categoriesTree.length > 0 ? (
          <div className="flex flex-col gap-1 max-h-48 overflow-y-auto">
            {categoriesTree.map((cat) => (
              <CategoryTreeItem
                key={cat.id}
                category={cat}
                selectedCategories={selectedCategories}
                onToggle={handleToggle}
                depth={0}
              />
            ))}
          </div>
        ) : categories.length > 0 ? (
          <div className="flex flex-col gap-2">
            {categories.map((c) => (
              <CheckboxWithLabel
                key={c.id}
                label={c.name}
                checked={selectedCategories.includes(c.id)}
                onChange={() => handleToggle("category", c.id, selectedCategories)}
              />
            ))}
          </div>
        ) : (
          <Text className="txt-compact-small text-ui-fg-muted">Sin opciones</Text>
        )}
      </AccordionSection>

      {/* Price range filter */}
      <AccordionSection
        title="Precio"
        isOpen={openSections.price}
        onToggle={() => setOpenSections((prev) => ({ ...prev, price: !prev.price }))}
      >
        <div className="flex gap-2 items-center">
          <div className="relative flex-1">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-ui-fg-muted text-sm">€</span>
            <input
              type="number"
              inputMode="decimal"
              min="0"
              step="0.01"
              placeholder="Min"
              value={minPriceInput}
              onChange={(e) => onPriceInputChange("min", e.target.value)}
              className="w-full pl-6 pr-2 py-1 border border-ui-border rounded text-sm"
            />
          </div>
          <span className="text-ui-fg-muted">-</span>
          <div className="relative flex-1">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-ui-fg-muted text-sm">€</span>
            <input
              type="number"
              inputMode="decimal"
              min="0"
              step="0.01"
              placeholder="Max"
              value={maxPriceInput}
              onChange={(e) => onPriceInputChange("max", e.target.value)}
              className="w-full pl-6 pr-2 py-1 border border-ui-border rounded text-sm"
            />
          </div>
        </div>
      </AccordionSection>

      {/* Tags filter */}
      <AccordionSection
        title="Tags"
        isOpen={openSections.tags}
        onToggle={() => setOpenSections((prev) => ({ ...prev, tags: !prev.tags }))}
      >
        {productTags.length > 0 ? (
          <div className="flex flex-col gap-2">
            {productTags.length > 10 && (
              <input
                type="text"
                placeholder="Buscar tag..."
                value={tagSearch}
                onChange={(e) => setTagSearch(e.target.value)}
                className="w-full px-2 py-1 border border-ui-border rounded text-sm mb-1 focus:outline-none focus:ring-1 focus:ring-ui-border-interactive"
              />
            )}
            {filteredTags.length > 0 ? (
              <div className="flex flex-col gap-2 max-h-48 overflow-y-auto">
                {filteredTags.map((tag) => (
                  <CheckboxWithLabel
                    key={tag}
                    label={tag}
                    checked={selectedTags.includes(tag)}
                    onChange={() => handleToggle("tags", tag, selectedTags)}
                  />
                ))}
              </div>
            ) : (
              <Text className="txt-compact-small text-ui-fg-muted">Sin resultados</Text>
            )}
          </div>
        ) : (
          <Text className="txt-compact-small text-ui-fg-muted">Sin opciones</Text>
        )}
      </AccordionSection>

      {/* Brand filter */}
      <AccordionSection
        title="Marca"
        isOpen={openSections.brand}
        onToggle={() => setOpenSections((prev) => ({ ...prev, brand: !prev.brand }))}
      >
        {productBrands.length > 0 ? (
          <div className="flex flex-col gap-2">
            {productBrands.length > 10 && (
              <input
                type="text"
                placeholder="Buscar marca..."
                value={brandSearch}
                onChange={(e) => setBrandSearch(e.target.value)}
                className="w-full px-2 py-1 border border-ui-border rounded text-sm mb-1 focus:outline-none focus:ring-1 focus:ring-ui-border-interactive"
              />
            )}
            {filteredBrands.length > 0 ? (
              <div className="flex flex-col gap-2 max-h-48 overflow-y-auto">
                {filteredBrands.map((b) => (
                  <CheckboxWithLabel
                    key={b}
                    label={b}
                    checked={selectedBrands.includes(b)}
                    onChange={() => handleToggle("brand", b, selectedBrands)}
                  />
                ))}
              </div>
            ) : (
              <Text className="txt-compact-small text-ui-fg-muted">Sin resultados</Text>
            )}
          </div>
        ) : (
          <Text className="txt-compact-small text-ui-fg-muted">Sin opciones</Text>
        )}
      </AccordionSection>
    </>
  )
}

const RefinementList = ({
  sortBy,
  'data-testid': dataTestId,
  collection,
  category,
  minPrice,
  maxPrice,
  tags,
  brand,
  filterData,
}: RefinementListProps) => {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    sort: true,
    collections: false,
    categories: true,
    price: false,
    tags: true,
    brand: true,
  })

  // Mobile drawer state
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false)

  const createQueryString = useCallback(
    (name: string, value: string) => {
      const params = new URLSearchParams(searchParams)
      if (value) {
        params.set(name, value)
      } else {
        params.delete(name)
      }
      // Al cambiar filtro/orden se vuelve a la página 1
      params.delete("page")
      return params.toString()
    },
    [searchParams]
  )

  const setQueryParams = (name: string, value: string) => {
    const query = createQueryString(name, value)
    router.push(`${pathname}?${query}`)
  }

  // Build current filter values
  const selectedCollections = collection ? collection.split(",").filter(Boolean) : []
  const selectedCategories = category ? category.split(",").filter(Boolean) : []
  const selectedBrands = brand ? brand.split(",").filter(Boolean) : []
  const selectedTags = tags ? tags.split(",").filter(Boolean) : []

  // Precio: estado local + debounce. Antes cada tecla lanzaba un
  // router.push con el valor a medio escribir ("-", "1e"), Meili recibía NaN
  // y el listado se quedaba vacío durante la escritura.
  const [minPriceInput, setMinPriceInput] = useState(minPrice || "")
  const [maxPriceInput, setMaxPriceInput] = useState(maxPrice || "")
  const lastAppliedPriceRef = useRef({ min: minPrice || "", max: maxPrice || "" })

  // Sincronizar con la URL (limpiar filtros, atrás/adelante, enlaces).
  useEffect(() => {
    const next = { min: minPrice || "", max: maxPrice || "" }
    if (
      next.min === lastAppliedPriceRef.current.min &&
      next.max === lastAppliedPriceRef.current.max
    ) {
      return
    }

    lastAppliedPriceRef.current = next
    setMinPriceInput(next.min)
    setMaxPriceInput(next.max)
  }, [minPrice, maxPrice])

  // Aplicar el precio ~450 ms después de la última tecla, y solo con valores
  // numéricos válidos. Evita navegaciones y búsquedas a Meili por tecla.
  useEffect(() => {
    if (
      minPriceInput === lastAppliedPriceRef.current.min &&
      maxPriceInput === lastAppliedPriceRef.current.max
    ) {
      return
    }

    const timer = setTimeout(() => {
      const min = minPriceInput.trim()
      const max = maxPriceInput.trim()
      if (!isValidPriceInput(min) || !isValidPriceInput(max)) return

      const params = new URLSearchParams(searchParams.toString())
      if (min) {
        params.set("minPrice", min)
      } else {
        params.delete("minPrice")
      }
      if (max) {
        params.set("maxPrice", max)
      } else {
        params.delete("maxPrice")
      }
      // Al cambiar el precio se vuelve a la página 1
      params.delete("page")

      lastAppliedPriceRef.current = { min, max }
      const query = params.toString()
      router.push(query ? `${pathname}?${query}` : pathname)
    }, 450)

    return () => clearTimeout(timer)
  }, [minPriceInput, maxPriceInput, searchParams, pathname, router])

  const handlePriceInputChange = (type: "min" | "max", value: string) => {
    if (type === "min") {
      setMinPriceInput(value)
    } else {
      setMaxPriceInput(value)
    }
  }

  // Toggle handlers for multi-select filters
  const handleToggle = (name: string, value: string, currentSelected: string[]) => {
    const params = new URLSearchParams(searchParams)
    if (currentSelected.includes(value)) {
      const newList = currentSelected.filter((v) => v !== value)
      if (newList.length > 0) {
        params.set(name, newList.join(","))
      } else {
        params.delete(name)
      }
    } else {
      params.set(name, [...currentSelected, value].join(","))
    }
    // Al cambiar un filtro multi-selección se vuelve a la página 1
    params.delete("page")
    router.push(`${pathname}?${params.toString()}`)
  }

  // Clear all filters
  const clearFilters = () => {
    const params = new URLSearchParams(searchParams)
    params.delete("collection")
    params.delete("category")
    params.delete("minPrice")
    params.delete("maxPrice")
    params.delete("tags")
    params.delete("brand")
    params.delete("page")
    router.push(`${pathname}?${params.toString()}`)
  }

  // Count active filters
  const activeFilterCount =
    (collection ? collection.split(",").filter(Boolean).length : 0) +
    (category ? category.split(",").filter(Boolean).length : 0) +
    (tags ? tags.split(",").filter(Boolean).length : 0) +
    (brand ? brand.split(",").filter(Boolean).length : 0) +
    (minPrice ? 1 : 0) +
    (maxPrice ? 1 : 0)

  // Desktop layout
  return (
    <>
      {/* Mobile filter button and drawer */}
      <div className="small:hidden">
        <Button
          variant="secondary"
          className="w-full flex items-center justify-between px-4 py-3"
          onClick={() => setIsMobileFiltersOpen(true)}
        >
          <span className="flex items-center gap-2">
            <SlidersHorizontal className="w-4 h-4" />
            Filtros
          </span>
          {activeFilterCount > 0 && (
            <span className="bg-[#f57aa9] text-white text-xs font-medium px-2 py-0.5 rounded-full">
              {activeFilterCount}
            </span>
          )}
        </Button>

        <Drawer open={isMobileFiltersOpen} onClose={() => setIsMobileFiltersOpen(false)}>
          <Drawer.Content className="bg-white h-full max-w-sm w-full">
            <Drawer.Header className="border-b border-ui-border flex items-center justify-between px-4 py-4">
              <Drawer.Title className="text-lg font-semibold">Filtros</Drawer.Title>
              <Drawer.Close asChild>
                <button className="p-2 hover:bg-gray-100 rounded-full">
                  <XMarkMini className="w-5 h-5" />
                </button>
              </Drawer.Close>
            </Drawer.Header>
            <Drawer.Body className="p-4 overflow-y-auto">
              <div className="flex flex-col gap-2">
                <FilterContent
                  sortBy={sortBy}
                  collection={collection}
                  category={category}
                  minPrice={minPrice}
                  maxPrice={maxPrice}
                  tags={tags}
                  brand={brand}
                  filterData={filterData}
                  onClearFilters={clearFilters}
                  setQueryParams={setQueryParams}
                  minPriceInput={minPriceInput}
                  maxPriceInput={maxPriceInput}
                  onPriceInputChange={handlePriceInputChange}
                  handleToggle={handleToggle}
                  selectedCollections={selectedCollections}
                  selectedCategories={selectedCategories}
                  selectedBrands={selectedBrands}
                  selectedTags={selectedTags}
                  openSections={openSections}
                  setOpenSections={setOpenSections}
                />
              </div>
            </Drawer.Body>
            <Drawer.Footer className="border-t border-ui-border p-4">
              <Button
                className="w-full bg-[#f57aa9] hover:bg-[#e56a97] border-none"
                onClick={() => setIsMobileFiltersOpen(false)}
              >
                Ver resultados ({activeFilterCount > 0 ? "filtrado" : "todos"})
              </Button>
            </Drawer.Footer>
          </Drawer.Content>
        </Drawer>
      </div>

      {/* Desktop sidebar layout */}
      <div className="hidden small:flex flex-col gap-4 py-4 mb-8 small:px-0 pl-6 small:min-w-[250px] small:ml-[1.675rem] small:shrink-0">
        <FilterContent
          sortBy={sortBy}
          dataTestId={dataTestId}
          collection={collection}
          category={category}
          minPrice={minPrice}
          maxPrice={maxPrice}
          tags={tags}
          brand={brand}
          filterData={filterData}
          onClearFilters={clearFilters}
          setQueryParams={setQueryParams}
          minPriceInput={minPriceInput}
          maxPriceInput={maxPriceInput}
          onPriceInputChange={handlePriceInputChange}
          handleToggle={handleToggle}
          selectedCollections={selectedCollections}
          selectedCategories={selectedCategories}
          selectedBrands={selectedBrands}
          selectedTags={selectedTags}
          openSections={openSections}
          setOpenSections={setOpenSections}
        />
      </div>
    </>
  )
}

export default RefinementList
